/**
 * Crossborder dashboard variants by home country.
 *
 * This module intentionally avoids runtime layout derivation from chart events.
 * Every country variant is precomputed from the known interconnector map.
 */
import { getCountryMetadata } from '@/config/domain/countryMetadata';
import { getInterconnectorMaxVolume } from '@/config/domain/crossborderInterconnectorCapacities';
import {
  getValidBorderCountries,
  VALID_INTERCONNECTORS,
} from '@/config/domain/crossborderInterconnectors';

const TOTAL_PANEL_ID = 'total-crossborder';
const EUROPE_MAP_PANEL_ID = 'crossborder-europe-map';
const BORDER_PANEL_PREFIX = 'crossborder';
const BORDER_PANEL_HEIGHT = 15;
const FIRST_ROW_EXTRA_HEIGHT = 5;
const TOTAL_PANEL_HEIGHT = 12;
const TOTAL_PANEL_WIDTH = 16;
const EUROPE_MAP_PANEL_WIDTH = 8;
const BORDER_PANEL_WIDTH = 8;
const BORDER_PANEL_COLS = 3;

const AVAILABLE_COUNTRIES = Object.keys(VALID_INTERCONNECTORS).sort();
const DEFAULT_COUNTRY = AVAILABLE_COUNTRIES.includes('NL') ? 'NL' : AVAILABLE_COUNTRIES[0] || null;

const normalizeCountryCode = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

const normalizeVariantCountry = (value) => {
  const candidate = normalizeCountryCode(value);
  if (candidate && AVAILABLE_COUNTRIES.includes(candidate)) {
    return candidate;
  }
  return DEFAULT_COUNTRY;
};

const buildBorderPanelId = (borderCountry) =>
  `${BORDER_PANEL_PREFIX}-${String(borderCountry || '').toLowerCase()}`;

const buildBorderPanelTitle = (homeCountry, borderCountry) => `${homeCountry}-${borderCountry}`;

const buildBorderPanelsForCountry = (homeCountry) => {
  const validBorders = getValidBorderCountries(homeCountry);

  return validBorders.map((borderCountry, index) => {
    const maxVolumeMw = getInterconnectorMaxVolume(homeCountry, borderCountry);
    const columnIndex = index % BORDER_PANEL_COLS;
    const rowIndex = Math.floor(index / BORDER_PANEL_COLS);
    const x = columnIndex * BORDER_PANEL_WIDTH;
    const y = FIRST_ROW_EXTRA_HEIGHT + TOTAL_PANEL_HEIGHT + rowIndex * BORDER_PANEL_HEIGHT;

    return {
      id: buildBorderPanelId(borderCountry),
      type: 'datasetChart',
      datasetId: 'entsoe-crossborder-net-flows',
      visualizationId: 'crossborder-net-flows-net-flows',
      gridPos: { x, y, w: BORDER_PANEL_WIDTH, h: BORDER_PANEL_HEIGHT },
      labelOverrides: {
        titlePrefix: buildBorderPanelTitle(homeCountry, borderCountry),
      },
      controlOverrides: {
        filters: {
          'home-country': homeCountry,
          'border-country': borderCountry,
        },
        ...(maxVolumeMw
          ? {
              axes: {
                yMin: -maxVolumeMw,
                yMax: maxVolumeMw,
              },
            }
          : {}),
        visibility: {
          filters: false,
          dateRange: false,
          granularity: false,
        },
      },
    };
  });
};

const buildTotalPanelForCountry = (homeCountry) => ({
  id: TOTAL_PANEL_ID,
  type: 'datasetChart',
  datasetId: 'entsoe-crossborder-totals',
  visualizationId: 'crossborder-totals-total-flows',
  gridPos: { x: 0, y: 0, w: TOTAL_PANEL_WIDTH, h: TOTAL_PANEL_HEIGHT + FIRST_ROW_EXTRA_HEIGHT },
  controlOverrides: {
    filters: {
      country: homeCountry,
    },
    visibility: {
      filters: false,
      dateRange: false,
      granularity: false,
    },
  },
});

const buildEuropeMapPanelForCountry = (homeCountry) => ({
  id: EUROPE_MAP_PANEL_ID,
  type: 'datasetEuropeMap',
  datasetId: 'entsoe-crossborder-flows-latest',
  visualizationId: 'crossborder-flows-latest-latest-flows-map',
  gridPos: {
    x: TOTAL_PANEL_WIDTH,
    y: 0,
    w: EUROPE_MAP_PANEL_WIDTH,
    h: TOTAL_PANEL_HEIGHT + FIRST_ROW_EXTRA_HEIGHT,
  },
  controlOverrides: {
    filters: {
      country: homeCountry,
      'home-country': homeCountry,
    },
  },
});

const buildCrossborderVariant = (homeCountry) => {
  const borderPanels = buildBorderPanelsForCountry(homeCountry);
  const borderPanelIds = borderPanels.map((panel) => panel.id);
  // The Europe map exposes the only visible dateRange/granularity controls in this
  // variant (the total + border panels hide them). Include it in the sync group so
  // changing period on any panel propagates to the rest.
  const syncedPanelIds = [TOTAL_PANEL_ID, EUROPE_MAP_PANEL_ID, ...borderPanelIds];

  return {
    country: homeCountry,
    panels: [
      buildTotalPanelForCountry(homeCountry),
      buildEuropeMapPanelForCountry(homeCountry),
      ...borderPanels,
    ],
    syncGroups: [
      {
        id: 'crossborder-controls-sync',
        global: false,
        ids: syncedPanelIds,
        syncDateRange: true,
        syncGranularity: true,
      },
    ],
  };
};

const crossborderVariants = Object.fromEntries(
  AVAILABLE_COUNTRIES.map((country) => [country, buildCrossborderVariant(country)])
);

const clone = (value) => JSON.parse(JSON.stringify(value));

export const getCrossborderCountryCodes = () => [...AVAILABLE_COUNTRIES];

export const getDefaultCrossborderCountry = () => DEFAULT_COUNTRY;

export const getCrossborderCountryOptions = () =>
  AVAILABLE_COUNTRIES.map((country) => {
    const metadata = getCountryMetadata(country);
    return {
      value: country,
      label: country,
      labelKey: metadata.labelKey,
      iconSrc: metadata.iconSrc,
    };
  });

export const normalizeCrossborderCountry = (value) => normalizeVariantCountry(value);

export const getCrossborderVariant = (value) => {
  const country = normalizeVariantCountry(value);
  const variant =
    (country && crossborderVariants[country]) || crossborderVariants[DEFAULT_COUNTRY] || null;
  if (!variant) {
    return { country: null, panels: [], syncGroups: [] };
  }

  return clone(variant);
};

export const flexCrossborderPanels = ({ country } = {}) => {
  return getCrossborderVariant(country).panels;
};

export const flexCrossborderSyncGroups = ({ country } = {}) => {
  return getCrossborderVariant(country).syncGroups;
};
